import React, { useState, useEffect } from 'react';
import LocalizedLink from './localizedLink';
import { Link } from 'gatsby';
import useTranslations from './../utils/useTranslations';
import { FaFacebook, FaTwitter, FaInstagram } from 'react-icons/fa';
import { IconContext } from 'react-icons';
import bittorrentLogo from '../images/logo-white.svg';
import utLogo from '../images/ut-logo-white.svg';
import HelpCenterLinks from './help-center/helpCenterLinks';
import packageInfo from '../../package.json';
import { XTwitterIcon } from '../utils/icons';

const version = packageInfo.version;

interface FooterProps {
    pageContext: {
        footerText: any;
    };
}

const Footer = ({ pageContext: { footerText } }: FooterProps) => {
    const t = useTranslations(footerText);
    const isBt = process.env.BRAND === 'bt';

    const [gdprComplianceRequired, setGdprComplianceRequired] = useState(false);

    useEffect(() => {
        window.didomiOnReady = window.didomiOnReady || [];
        window.didomiOnReady.push(function (Didomi) {
            setGdprComplianceRequired(Didomi.isRegulationApplied('gdpr') || Didomi.isRegulationApplied('ccpa'));
        });
    }, []);

    const privacyPreferencesClick = () => {
        window.Didomi.preferences.show();
    };

    return (
        <>
            <footer className="tw-relative tw-bg-footer tw-text-white">
                <div className="container pt-5 pb-3">
                    <div className="tw-group tw-grid md:tw-grid-cols-6" data-brand={process.env.BRAND}>
                        <div
                            className="tw-col-span-1 tw-mb-6 tw-flex tw-justify-center md:tw-mb-0 md:tw-justify-start
                                md:group-data-[brand=ut]:tw-col-span-2"
                        >
                            {isBt ? (
                                <LocalizedLink className="tw-h-[40px]" to="/" id="footer-bt-logo">
                                    <img className="tw-h-[30px]" src={bittorrentLogo} alt="BitTorrent Footer Logo" />
                                </LocalizedLink>
                            ) : (
                                <LocalizedLink className="tw-h-[40px]" to="/" id="footer-bt-logo">
                                    <img className="tw-h-[40px]" src={utLogo} alt="µTorrent Footer Logo" />
                                </LocalizedLink>
                            )}
                        </div>
                        <div
                            className="tw-col-span-1 tw-mx-auto tw-grid tw-grid-cols-2 tw-gap-x-12 tw-text-[80%]
                                min-[400px]:tw-gap-x-28 sm:tw-mx-0 sm:tw-grid-cols-4 sm:tw-gap-x-12
                                sm:group-data-[brand=ut]:tw-grid-cols-3 sm:group-data-[brand=ut]:tw-gap-x-24
                                md:tw-col-span-4 md:tw-gap-x-0 md:tw-pl-6 md:group-data-[brand=ut]:tw-col-span-3
                                md:group-data-[brand=ut]:tw-gap-x-0 md:group-data-[brand=ut]:tw-pl-0 lg:tw-pl-0"
                        >
                            <div className="tw-col-span-1 tw-text-left">
                                <h6 id="company" className="">
                                    {t('Company')}
                                </h6>
                                <ul>
                                    <li>
                                        {isBt ? (
                                            <LocalizedLink to="/company/about-us/" id="footer-co-about">
                                                {t('About')}
                                            </LocalizedLink>
                                        ) : (
                                            <a
                                                href="https://www.bittorrent.com/company/about-us/"
                                                id="footer-co-about"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('About')}
                                            </a>
                                        )}
                                    </li>
                                    <li>
                                        {isBt ? (
                                            <LocalizedLink to="/company/contact-us/" id="footer-co-contact">
                                                {t('Contact')}
                                            </LocalizedLink>
                                        ) : (
                                            <a
                                                href="https://www.bittorrent.com/company/contact-us/"
                                                id="footer-co-contact"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Contact')}
                                            </a>
                                        )}
                                    </li>
                                    <li>
                                        {isBt ? (
                                            <LocalizedLink to="/pressroom/" id="footer-co-press">
                                                {t('Press')}
                                            </LocalizedLink>
                                        ) : (
                                            <a
                                                href="https://www.bittorrent.com/blog/pressroom/"
                                                id="footer-co-press"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Press')}
                                            </a>
                                        )}
                                    </li>
                                    <li>
                                        {isBt ? (
                                            <LocalizedLink to="/company/jobs/" id="footer-co-jobs">
                                                {t('Jobs')}
                                            </LocalizedLink>
                                        ) : (
                                            <a
                                                href="https://www.bittorrent.com/company/jobs/"
                                                id="footer-co-jobs"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Jobs')}
                                            </a>
                                        )}
                                    </li>
                                    <li>
                                        {isBt ? (
                                            <Link to="/blog/" id="footer-co-blog">
                                                {t('Blog')}
                                            </Link>
                                        ) : (
                                            <a
                                                href="https://blog.utorrent.com"
                                                id="footer-co-blog"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Blog')}
                                            </a>
                                        )}
                                    </li>
                                </ul>
                            </div>
                            <div className="tw-col-span-1 tw-text-left">
                                <h6 id="downloads" className="">
                                    {t('Downloads')}
                                </h6>
                                <ul>
                                    <li>
                                        <LocalizedLink to="/downloads/windows/" id="footer-dl-windows">
                                            {t('Windows')}
                                        </LocalizedLink>
                                    </li>
                                    <li>
                                        <LocalizedLink to="/downloads/mac/" id="footer-dl-mac">
                                            {t('Mac')}
                                        </LocalizedLink>
                                    </li>
                                    <li>
                                        <LocalizedLink to="/downloads/android/" id="footer-dl-android">
                                            {t('Android')}
                                        </LocalizedLink>
                                    </li>
                                    {!isBt && (
                                        <li>
                                            <LocalizedLink to="/downloads/linux/" id="footer-dl-linux">
                                                {t('Linux')}
                                            </LocalizedLink>
                                        </li>
                                    )}
                                </ul>
                            </div>
                            {isBt && (
                                <div className="tw-col-span-1 tw-text-left">
                                    <h6 id="platforms" className="">
                                        {t('Platforms')}
                                    </h6>
                                    <ul>
                                        <li>
                                            <LocalizedLink to="/platforms/windows/" id="footer-pl-windows">
                                                {t('Windows')}
                                            </LocalizedLink>
                                        </li>
                                        <li>
                                            <LocalizedLink to="/platforms/mac/" id="footer-pl-mac">
                                                {t('Mac')}
                                            </LocalizedLink>
                                        </li>
                                        <li>
                                            <LocalizedLink to="/platforms/android/" id="footer-pl-android">
                                                {t('Android')}
                                            </LocalizedLink>
                                        </li>
                                    </ul>
                                </div>
                            )}
                            <div className="tw-col-span-1 tw-text-left">
                                <h6 id="help" className="">
                                    {t('Support')}
                                </h6>
                                <ul>
                                    <li>
                                        {isBt ? (
                                            <LocalizedLink to={HelpCenterLinks.homeLink} id="footer-sup-help">
                                                {t('Help')}
                                            </LocalizedLink>
                                        ) : (
                                            <a
                                                href="https://bittorrent.com/help/"
                                                target="_blank"
                                                id="footer-sup-help"
                                                rel="noopener noreferrer"
                                            >
                                                {t('Help')}
                                            </a>
                                        )}
                                    </li>
                                    <li>
                                        <a
                                            href={
                                                isBt ? 'https://forum.bittorrent.com/' : 'https://forum.utorrent.com/'
                                            }
                                            target="_blank"
                                            id="footer-sup-forum"
                                            rel="noopener noreferrer"
                                        >
                                            {t('Forum')}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://forms.gle/AXnnDpFRDSo2A84j6"
                                            target="_blank"
                                            id="footer-sup-feedback"
                                            rel="noopener noreferrer"
                                        >
                                            {t('Site Feedback')}
                                        </a>
                                    </li>
                                    {isBt && (
                                        <li>
                                            <Link to={`/tip-of-the-day`} id="footer-tip-of-the-day">
                                                {'Tip of the Day'}
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                        <div className="tw-col-span-1 tw-px-3 tw-text-center md:tw-text-left">
                            <div className="d-flex align-items-start justify-content-center mt-4 mb-2 mb-md-0 mt-md-0">
                                <IconContext.Provider value={{ className: 'socialicon' }}>
                                    <a
                                        id="footer-social-fb"
                                        href="https://facebook.com/bittorrent"
                                        className="facebook footer-social-alt"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaFacebook className="footer-social-icon" />
                                        Facebook
                                    </a>
                                    <a
                                        id="footer-social-twitter"
                                        href="https://x.com/BitTorrent"
                                        className="twitter ml-3 footer-social-alt"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <XTwitterIcon className="footer-social-icon" />
                                        Twitter
                                    </a>
                                    <a
                                        id="footer-social-instagram"
                                        href="https://instagram.com/bittorrent"
                                        className="instagram ml-3 footer-social-alt"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaInstagram className="footer-social-icon" />
                                        Instagram
                                    </a>
                                </IconContext.Provider>
                            </div>
                        </div>
                    </div>

                    <hr
                        style={{
                            margin: `2.5rem 0`,
                            borderTop: `1px solid gray`,
                        }}
                    />

                    <div className="row mb-2 mb-sm-4">
                        <div
                            className="col-12 col-md-5 order-2 order-md-1 mt-2 mt-md-2 mb-4 mb-md-0
                                justify-content-center justify-content-md-start small d-flex"
                        >
                            Version {version} &copy; {new Date().getFullYear()}{' '}
                            {isBt
                                ? `BitTorrent Limited ${t('All Rights Reserved')}.`
                                : t(`µTorrent is a trademark of BitTorrent Limited.`)}
                        </div>
                        <div
                            className="col-12 col-md-7 order-1 order-md-2 mt-0 mt-md-2 mb-1 mb-md-0 text-center
                                text-md-right small"
                        >
                            {isBt ? (
                                <Link to="/legal/eula/" id="footer-co-eula">
                                    {t('EULA')}
                                </Link>
                            ) : (
                                <a
                                    href="https://www.bittorrent.com/legal/eula/"
                                    id="footer-co-eula"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('EULA')}
                                </a>
                            )}
                            <span className="footer-vertical-line"> | </span>
                            {isBt ? (
                                <Link to="/legal/copyright/" id="footer-co-copyright">
                                    {t('Copyright')}
                                </Link>
                            ) : (
                                <a
                                    href="https://www.bittorrent.com/legal/copyright/"
                                    id="footer-co-copyright"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('Copyright')}
                                </a>
                            )}
                            <span className="footer-vertical-line"> | </span>
                            {isBt ? (
                                <Link to="/legal/terms-of-use/" id="footer-co-tos">
                                    {t('termsOfUse')}
                                </Link>
                            ) : (
                                <a
                                    href="https://www.bittorrent.com/legal/terms-of-use/"
                                    id="footer-co-tos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('termsOfUse')}
                                </a>
                            )}
                            <span className="footer-vertical-line"> | </span>
                            {isBt ? (
                                <Link to="/legal/privacy-policy/" id="footer-co-privacy">
                                    {t('Privacy')}
                                </Link>
                            ) : (
                                <a
                                    href="https://www.bittorrent.com/legal/privacy-policy/"
                                    id="footer-co-privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('Privacy')}
                                </a>
                            )}
                            {gdprComplianceRequired && window.cmpScriptLoaded && (
                                <>
                                    <span className="footer-vertical-line"> | </span>
                                    <button
                                        onClick={privacyPreferencesClick}
                                        id="footer-co-privacy-pref"
                                        className="d-inline btn-no-styling"
                                    >
                                        {t('Privacy Preferences')}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer;
